// Copied to mju-aioe/packages/shared/src/schemas/a2a.ts — keep the two identical (zod only, no other imports).
/**
 * Microsoft 365 Copilot and Copilot Studio call our specialists and teams over
 * Agent2Agent (A2A). The organisation (AIOE) is the A2A server: it publishes
 * the agent cards, authenticates the caller (a delegated Entra token for the
 * person, scope `Agents.Invoke`), decides where the work runs, and hands it to
 * a workbench or a node over the relay as the Control API methods below. The
 * workbench routes it — a specialist answers in a Chat conversation, a team
 * works it in Team Work — and reports progress back up the relay as
 * `a2a.event` events, which the organisation turns into A2A task status and
 * artifact updates. Plans: mju-aioe docs/plans/2026-10-copilot-calls-our-agents.md
 * (the organisation's half and JP's decisions) and the workbench's
 * docs/plans/2026-10-copilot-a2a.md.
 *
 * Methods (JSON-RPC over the relay, signed by the organisation for the person):
 *
 *   a2a.message  {@link A2aMessageParams} → {@link A2aMessageAnswer}
 *   a2a.reply    {@link A2aReplyParams}   → {@link A2aAck}   answers `input-required`
 *   a2a.cancel   {@link A2aCancelParams}  → {@link A2aAck}   the workbench's Stop
 *
 * Events on the relay's event stream: kind `a2a.event`, payload {@link A2aEvent}
 * (a desktop's gateway stamps `workspace` and `sourceSeq` onto every payload;
 * they are not part of the event and parse away).
 *
 * **Scopes** ({@link A2A_SCOPES}). The organisation signs each call for the
 * person with the scopes the workbench requires: `tasks:write` for every
 * method (the Control API's own check, as for `cloudMcp.call` and
 * `tasks.dispatch`: the device must have been granted task access), plus
 * `prompts:write` (a message goes into a conversation, as `facilitator.send`)
 * and, for a team, `agents:write` (Team Work starts the team's members, as
 * `tasks.dispatch` and `board.execute` do). A call missing one is refused
 * `not_allowed`, before anything starts.
 *
 * **Policy** ({@link a2aPolicySchema}, the `a2a` clause of the organisation's
 * policy). Enterprise only: a workbench refuses every call unless its policy is
 * the organisation's (managed) and the clause says `allowed: true`; `callers`
 * narrows it to named Copilot Studio agents (their application ids);
 * `requireApproval` holds the work for the person — `always` before it starts
 * (a team's plan waits for approval, a specialist waits for a yes in the
 * Inbox), `changes` before anything it changed merges. Left out: no hold.
 *
 * **Caps** ({@link A2A_LIMITS}). Text parts together at most 100 000
 * characters; at most 20 parts, 10 of them files. A file part carries either
 * `bytes` (base64, at most 5 MiB decoded each, 10 MiB for the message) or an
 * `https:` `uri` — never both, never another scheme. The workbench never
 * downloads a `uri`: it is handed to the agent as a link, which the agent may
 * open under the project's network policy. A data part is at most 20 000
 * characters of JSON. Anything over is refused `invalid_request`.
 *
 * Words for people: "Asked from Microsoft 365 Copilot (<caller name>)".
 */
import { z } from "zod";
export const A2A_SCHEMA_VERSION = 1;
/** The caps the workbench applies (and the organisation should apply before relaying). Bytes are decoded bytes. */
export const A2A_LIMITS = {
    /** All text parts of one message together, in characters. */
    textChars: 100_000,
    /** Parts in one message. */
    parts: 20,
    /** File parts in one message. */
    fileParts: 10,
    /** One file's bytes, decoded. */
    fileBytes: 5 * 1024 * 1024,
    /** Every file's bytes in one message, decoded. */
    totalFileBytes: 10 * 1024 * 1024,
    /** One data part, as JSON. */
    dataChars: 20_000,
    /** A message's `metadata`, as JSON. */
    metadataChars: 4_000,
    /** A file's `uri`. */
    uriChars: 2_048,
    /** A file's name and media type. */
    nameChars: 255,
    /** Ids: messages, tasks, contexts, artifacts, questions, refs. */
    idChars: 200,
    /** One text part of what the workbench sends up (a reply, a progress line, an artifact's text). */
    replyTextChars: 200_000,
    /** A question's words; its options, each and how many. */
    questionChars: 2_000,
    optionChars: 200,
    options: 8,
    /** Links on one artifact. */
    links: 20,
};
/** The base64 alphabet, padded (RFC 4648 §4): a flat character class, so a 7 MB string is checked without backtracking. */
const BASE64_CHARS_RE = /^[A-Za-z0-9+/]*={0,2}$/;
/** Is this padded base64 (RFC 4648 §4), with no whitespace? */
export function isBase64(s) {
    return s.length % 4 === 0 && BASE64_CHARS_RE.test(s);
}
/** Decoded size of a base64 string ({@link isBase64}). */
export function base64Bytes(b64) {
    const pad = b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0;
    return (b64.length / 4) * 3 - pad;
}
const id = z.string().min(1).max(A2A_LIMITS.idChars);
const jsonChars = (value) => {
    try {
        return JSON.stringify(value)?.length ?? 0;
    }
    catch {
        return Number.POSITIVE_INFINITY;
    }
};
const record = z.record(z.string(), z.unknown());
const metadata = record.refine((m) => jsonChars(m) <= A2A_LIMITS.metadataChars, `metadata is at most ${A2A_LIMITS.metadataChars} characters of JSON`);
/** A specialist's or a team's id in the workbench (a team file's `id`). */
export const A2A_REF_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,99}$/;
export const a2aRefSchema = z.string().regex(A2A_REF_RE, "a specialist's or team's id: letters, digits, dot, dash and underscore, at most 100");
/** Which of the two routes: a specialist answers in a conversation; a team works it in Team Work. */
export const a2aKindSchema = z.enum(["specialist", "team"]);
// ── Parts and messages (A2A 0.3 `Part`, `Message`) ─────────────────────────
export const a2aTextPartSchema = z.object({
    kind: z.literal("text"),
    text: z.string().max(A2A_LIMITS.replyTextChars),
    metadata: metadata.optional(),
});
/**
 * A file: `bytes` (base64) under the cap, or an `https:` `uri` the workbench
 * does not download. Exactly one of the two.
 */
export const a2aFileSchema = z
    .object({
    name: z.string().min(1).max(A2A_LIMITS.nameChars).optional(),
    mimeType: z.string().min(1).max(A2A_LIMITS.nameChars).optional(),
    bytes: z
        .string()
        .max(Math.ceil(A2A_LIMITS.fileBytes / 3) * 4)
        .refine(isBase64, "bytes are base64")
        .optional(),
    uri: z
        .string()
        .max(A2A_LIMITS.uriChars)
        .refine((u) => {
        try {
            return new URL(u).protocol === "https:";
        }
        catch {
            return false;
        }
    }, "a file's uri is an https: address")
        .optional(),
})
    .refine((f) => (f.bytes === undefined) !== (f.uri === undefined), "a file carries either bytes or a uri, not both and not neither")
    .refine((f) => f.bytes === undefined || base64Bytes(f.bytes) <= A2A_LIMITS.fileBytes, `a file is at most ${A2A_LIMITS.fileBytes} bytes`);
export const a2aFilePartSchema = z.object({ kind: z.literal("file"), file: a2aFileSchema, metadata: metadata.optional() });
export const a2aDataPartSchema = z.object({
    kind: z.literal("data"),
    data: record.refine((d) => jsonChars(d) <= A2A_LIMITS.dataChars, `a data part is at most ${A2A_LIMITS.dataChars} characters of JSON`),
    metadata: metadata.optional(),
});
export const a2aPartSchema = z.discriminatedUnion("kind", [a2aTextPartSchema, a2aFilePartSchema, a2aDataPartSchema]);
/** The totals a message must keep to (each part's own caps are on the part). */
function messageTotals(parts, ctx) {
    let text = 0;
    let files = 0;
    let bytes = 0;
    for (const p of parts) {
        if (p.kind === "text")
            text += p.text.length;
        else if (p.kind === "file") {
            files++;
            if (p.file.bytes)
                bytes += base64Bytes(p.file.bytes);
        }
    }
    if (text > A2A_LIMITS.textChars)
        ctx.addIssue({ code: "custom", message: `the message's text is at most ${A2A_LIMITS.textChars} characters`, path: ["parts"] });
    if (files > A2A_LIMITS.fileParts)
        ctx.addIssue({ code: "custom", message: `a message carries at most ${A2A_LIMITS.fileParts} files`, path: ["parts"] });
    if (bytes > A2A_LIMITS.totalFileBytes)
        ctx.addIssue({ code: "custom", message: `a message's files are at most ${A2A_LIMITS.totalFileBytes} bytes together`, path: ["parts"] });
}
/** The person's message, as Copilot sent it (A2A `Message`, role `user`). */
export const a2aMessageSchema = z
    .object({
    kind: z.literal("message").optional(),
    role: z.literal("user"),
    messageId: id,
    parts: z.array(a2aPartSchema).min(1).max(A2A_LIMITS.parts),
    contextId: id.optional(),
    taskId: id.optional(),
    metadata: metadata.optional(),
})
    .superRefine((m, ctx) => messageTotals(m.parts, ctx));
/** What the workbench says (A2A `Message`, role `agent`): text parts — a progress line, a question, the answer — and data. */
export const a2aAgentMessageSchema = z.object({
    kind: z.literal("message").optional(),
    role: z.literal("agent"),
    messageId: id,
    parts: z.array(z.discriminatedUnion("kind", [a2aTextPartSchema, a2aDataPartSchema])).min(1).max(A2A_LIMITS.parts),
    contextId: id.optional(),
    taskId: id.optional(),
});
// ── Who is calling ─────────────────────────────────────────────────────────
/** The Copilot Studio agent that called: its Entra application id (the token's `azp`/`appid`), and its name when known. */
export const a2aCallerSchema = z.object({
    appId: id,
    name: z.string().min(1).max(200).optional(),
});
/**
 * The origin a WorkbenchControl actor carries for work asked over A2A: it is on
 * every audit row the call causes, and on the conversation the person sees.
 */
export const a2aOriginSchema = z.object({
    source: z.literal("a2a"),
    /** The caller's Entra application id. */
    caller: id,
    callerName: z.string().min(1).max(200).optional(),
});
/** The `workspace` the desktop's gateway routes on: a workspace (root) id, or `{ root, target }`. */
export const a2aWorkspaceSchema = z.union([z.string().min(1).max(500), z.object({ root: z.string().min(1).max(4_000), target: z.string().min(1).max(200).optional() })]);
// ── a2a.message ────────────────────────────────────────────────────────────
/**
 * `a2a.message`: a new A2A task for a specialist or a team on this workbench.
 * `taskId` is the organisation's A2A task id and keys every event; sent again
 * with a task the workbench already holds, it answers the same `runRef`
 * (a retried call starts nothing twice). `contextId` (A2A's conversation)
 * carries a later task on in the conversation an earlier task of the same
 * context and ref opened, once that one has finished. `projectId` is the
 * project the organisation chose, for the record.
 *
 * **Who answers.** `workspace` is the fleet's `workspaces[].id` (the string
 * form) of a repository of the project; `ref` is resolved in that
 * repository's teams, and a ref not there is `not_found`.
 *  - `kind: "team"` — `ref` is the organisation team's id (`OrgTeam.id`, the
 *    team's id on the workbench). Team Work runs with that team.
 *  - `kind: "specialist"` with `member` — `ref` is the organisation team's id
 *    and `member` one of its members by name (case-sensitive, never a human
 *    member). It runs as a one-to-one Chat with that member: its role's
 *    instructions, its provider and model, its Entra agent identity (so
 *    Agent 365 names that agent). A member the agent registry has disabled or
 *    quarantined is `not_allowed`.
 *  - `kind: "specialist"` without `member` — `ref` is a workbench specialist's
 *    id (a specialist defined in the repository, the project or the machine).
 * `member` is allowed only with `kind: "specialist"`.
 */
export const a2aMessageParamsSchema = z
    .object({
    workspace: a2aWorkspaceSchema,
    taskId: id,
    ref: a2aRefSchema,
    kind: a2aKindSchema,
    /** With `kind: "specialist"`: the member of the team `ref` that answers, by name. */
    member: z.string().min(1).max(100).optional(),
    message: a2aMessageSchema,
    contextId: id.optional(),
    projectId: id.optional(),
    caller: a2aCallerSchema,
})
    .refine((p) => p.member === undefined || p.kind === "specialist", { message: "member is only for kind specialist", path: ["member"] });
/**
 * Why a call was not taken:
 *  - `not_found` — no such specialist or team here (or, for reply / cancel, no such task);
 *  - `not_allowed` — the policy (no `a2a` clause, `allowed` false, a caller not in `callers`), the edition (not
 *    Enterprise), a call with no person behind it, or a scope the call was not signed with;
 *  - `busy` — the workbench already holds 20 open A2A tasks (as many as it takes at once), or, rarely, the
 *    conversation was still taking another message (a facilitator collision); worth trying elsewhere;
 *  - `invalid_request` — the params do not parse (caps included), or a reply to a task that asks nothing;
 *  - `node_cannot_spawn` — a node runs only the specialists and team members already running on it, and never
 *    starts one: a team that is not running there.
 */
export const A2A_REFUSALS = ["not_found", "not_allowed", "busy", "invalid_request", "node_cannot_spawn"];
export const a2aRefusalSchema = z.enum(A2A_REFUSALS);
const refused = z.object({ refusal: a2aRefusalSchema, message: z.string().min(1).max(1_000) });
/** `runRef`: the workbench's conversation the work runs in (a Chat or Team Work conversation id). */
export const a2aMessageAnswerSchema = z.union([
    z.object({ accepted: z.literal(true), runRef: id }),
    refused.extend({ accepted: z.literal(false) }),
]);
// ── a2a.reply, a2a.cancel ──────────────────────────────────────────────────
/**
 * `a2a.reply`: the person's answer to a task in `input-required`. With
 * `questionId` (the `question.id` of that status) it answers that question —
 * an `ask_human` decision is answered as if in the workbench (an option's
 * label or id picks it; any other text is the answer's note, or the answer to
 * a free-text question); without it, the text goes into the conversation as
 * the person's next message.
 */
export const a2aReplyParamsSchema = z.object({
    workspace: a2aWorkspaceSchema,
    taskId: id,
    message: a2aMessageSchema,
    questionId: id.optional(),
});
/** `a2a.cancel`: the workbench's Stop for that conversation or run; the task ends `canceled`. */
export const a2aCancelParamsSchema = z.object({ workspace: a2aWorkspaceSchema, taskId: id });
export const a2aAckSchema = z.union([z.object({ ok: z.literal(true) }), refused.extend({ ok: z.literal(false) })]);
// ── a2a.event ──────────────────────────────────────────────────────────────
/** A2A `TaskState`, as far as the workbench reports it (`rejected` and `auth-required` are the organisation's). */
export const A2A_STATES = ["submitted", "working", "input-required", "completed", "canceled", "failed"];
export const a2aStateSchema = z.enum(A2A_STATES);
/** What `input-required` asks: the `ask_human` decision's id and words, and its options' labels when it has some. */
export const a2aQuestionSchema = z.object({
    id,
    text: z.string().min(1).max(A2A_LIMITS.questionChars),
    options: z.array(z.string().min(1).max(A2A_LIMITS.optionChars)).max(A2A_LIMITS.options).optional(),
});
/** A link on an artifact: the branch or pull request a team's work is on, a file it made, any other address. */
export const a2aLinkSchema = z.object({
    kind: z.enum(["branch", "pull-request", "file", "url"]),
    label: z.string().min(1).max(300),
    href: z.string().min(1).max(A2A_LIMITS.uriChars),
});
/** A2A `Artifact`, plus `links` (where to find it). */
export const a2aArtifactSchema = z.object({
    artifactId: id,
    name: z.string().min(1).max(300),
    description: z.string().max(2_000).optional(),
    parts: z.array(a2aPartSchema).max(A2A_LIMITS.parts),
    links: z.array(a2aLinkSchema).max(A2A_LIMITS.links).optional(),
});
const eventCommon = {
    /** The organisation's A2A task id. */
    taskId: id,
    /** The conversation the work runs in. */
    runRef: id,
    /** 1, 2, 3 … per task, in the order the workbench emitted them (the relay may batch; it does not reorder). */
    seq: z.number().int().min(1),
    /** ISO 8601. */
    at: z.string().min(1).max(64),
};
/**
 * A2A `TaskStatusUpdateEvent`. `message` is what the agent said: a progress
 * line while `working`, the question while `input-required`, the answer or
 * the run's summary when `completed`, why when `failed`. `final` is true on
 * the last event of the task (`completed`, `canceled`, `failed`).
 */
export const a2aStatusEventSchema = z.object({
    ...eventCommon,
    type: z.literal("status"),
    state: a2aStateSchema,
    message: a2aAgentMessageSchema.optional(),
    question: a2aQuestionSchema.optional(),
    final: z.boolean().optional(),
});
/** A2A `TaskArtifactUpdateEvent`: something the work made (a file, a picture, a document; a team's branch or pull request). Sent before the final status. */
export const a2aArtifactEventSchema = z.object({
    ...eventCommon,
    type: z.literal("artifact"),
    artifact: a2aArtifactSchema,
    append: z.boolean().optional(),
    lastChunk: z.boolean().optional(),
});
export const a2aEventSchema = z.discriminatedUnion("type", [a2aStatusEventSchema, a2aArtifactEventSchema]);
/** The event ring kind the events travel under. */
export const A2A_EVENT_KIND = "a2a.event";
// ── Scopes and policy ──────────────────────────────────────────────────────
/** The scopes the organisation signs each method with (see the header for why). */
export const A2A_SCOPES = {
    "a2a.message": { specialist: ["tasks:write", "prompts:write"], team: ["tasks:write", "prompts:write", "agents:write"] },
    "a2a.reply": ["tasks:write", "prompts:write"],
    "a2a.cancel": ["tasks:write", "prompts:write"],
};
/**
 * The `a2a` clause of the organisation's policy. Left out of a managed policy,
 * nothing on the workbench is callable from Microsoft 365. `callers` lists the
 * Copilot Studio agents (Entra application ids) that may call; absent, any the
 * organisation lets through. `requireApproval`: `always` holds every task for
 * the person before it starts, `changes` before what it changed merges.
 */
export const a2aPolicySchema = z.object({
    allowed: z.boolean(),
    callers: z.array(id).max(100).optional(),
    requireApproval: z.enum(["always", "changes"]).optional(),
});
//# sourceMappingURL=a2a.js.map